import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { readPublicStatus, checkPublicAvailability, validatePublicStatus } from '../services/serviceAvailabilityService';
import { subscribeAvailabilityErrors } from '../utils/serviceAvailabilityEvents';

const Context = createContext(null);
const BACKOFF = [5000, 15000, 30000, 60000];
const FRESH_MS = 120000;

export function ServiceAvailabilityProvider({ children }) {
  const [value, setValue] = useState({ available: null, offline: !navigator.onLine, maintenance: null, checking: false });
  const actions = useRef({ retry: () => {} });
  const retry = useCallback(() => actions.current.retry(), []);
  useEffect(() => {
    let alive = true, pending = null, timer = null, generation = 0, failures = 0;
    let available = null, maintenance = null, offline = !navigator.onLine;
    const publish = checking => { if (alive) setValue({ available, maintenance, offline, checking }); };
    const canProbe = () => alive && document.visibilityState !== 'hidden' && !offline;
    const schedule = () => {
      clearTimeout(timer);
      if (!canProbe()) return;
      timer = setTimeout(() => check(available === false || Boolean(maintenance)), available === false ? BACKOFF[Math.min(Math.max(0, failures - 1), BACKOFF.length - 1)] : 60000);
    };
    const check = (withAPI = true) => {
      if (!canProbe()) return Promise.resolve();
      if (pending) {
        // A status-only request cannot answer API reachability. Remember one
        // stronger demand without queuing duplicate probes or invalidating an
        // API-inclusive attempt that already answers ordinary overlapping errors.
        if (withAPI && !pending.withAPI) pending.needsAPI = true;
        return pending.promise;
      }
      clearTimeout(timer);
      const attempt = { generation, withAPI, needsAPI: false, controller: new AbortController(), promise: null };
      pending = attempt;
      if (maintenance && performance.now() - maintenance.checkedAt > FRESH_MS) maintenance = { ...maintenance, stale: true };
      publish(true);
      attempt.promise = Promise.allSettled([
        readPublicStatus(attempt.controller.signal),
        withAPI ? checkPublicAvailability(attempt.controller.signal) : Promise.resolve(null)
      ]).then(([statusResult, apiResult]) => {
        if (!alive || offline) return;
        const checkedAt = performance.now();
        const status = statusResult.status === 'fulfilled' ? validatePublicStatus(statusResult.value) : null;
        const apiFailure = withAPI && apiResult.status === 'rejected';
        const error = apiFailure ? apiResult.reason : null;
        const explicitAPI = error?.response?.status === 503
          && (error.response.data?.error?.code || error.response.data?.code) === 'SERVICE_MAINTENANCE';
        if (attempt.generation !== generation) {
          // New explicit incidents fence an older successful recovery, but they do
          // not invalidate failed public reachability evidence. Discarding that
          // evidence would let steady app traffic suppress the outage notice
          // forever and continually restart probes instead of backing off.
          if (apiFailure) {
            available = false; failures += 1; attempt.acceptedFailure = true;
            publish(false);
          }
          return;
        }
        if (withAPI) {
          available = !apiFailure;
          failures = available ? 0 : failures + 1;
        }
        // Only a freshly retrieved explicit declaration proves maintenance.
        // A successful database probe cannot clear a newer or stale declaration;
        // recovery needs fresh normal status plus a successful API check.
        if (status?.state === 'maintenance') maintenance = { ...status, checkedAt, stale: false };
        else if (explicitAPI) maintenance = { message: {}, expectedRecoveryAt: null, checkedAt, stale: false };
        else if (status?.state === 'normal') maintenance = null;
        else if (maintenance && !status) maintenance = { ...maintenance, stale: true };
        publish(false);
      }).finally(() => {
        if (pending === attempt) pending = null;
        if (!alive) return;
        if (attempt.needsAPI || (attempt.generation !== generation && !attempt.acceptedFailure)) { void check(true); return; }
        schedule();
      });
      return attempt.promise;
    };
    const unsubscribe = subscribeAvailabilityErrors(event => {
      if (event.code === 'SERVICE_MAINTENANCE') {
        generation += 1;
        maintenance = { message: {}, expectedRecoveryAt: null, checkedAt: performance.now(), stale: false };
        publish(Boolean(pending));
      }
      // Only explicit maintenance invalidates older recovery evidence. Ordinary
      // feature/network failures merely request a coalesced independent probe;
      // invalidating every result could starve both outage and recovery forever.
      // There is never an automatic authenticated retry.
      // Once unavailable, ordinary traffic must respect the scheduled backoff.
      // Explicit focus/manual/online actions can still check recovery promptly.
      if (available !== false || pending) void check(true);
    });
    const onFocus = () => {
      if (document.visibilityState === 'hidden') { clearTimeout(timer); return; }
      if (maintenance) maintenance = { ...maintenance, stale: true };
      publish(Boolean(pending)); void check(true);
    };
    const onOffline = () => {
      // An offline browser cannot confirm that a prior declaration is current.
      // No timer/probe runs offline, so invalidate freshness at this transition.
      if (maintenance) maintenance = { ...maintenance, stale: true };
      offline = true; generation += 1; clearTimeout(timer); pending?.controller.abort(); publish(false);
    };
    const onOnline = () => { offline = false; publish(false); void check(true); };
    const manualRetry = () => { offline = !navigator.onLine; failures = 0; publish(Boolean(pending)); return check(true); };
    actions.current.retry = manualRetry;
    window.addEventListener('focus', onFocus); document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('offline', onOffline); window.addEventListener('online', onOnline);
    void check(true);
    return () => {
      alive = false; generation += 1; clearTimeout(timer); pending?.controller.abort(); unsubscribe();
      window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('offline', onOffline); window.removeEventListener('online', onOnline);
    };
  }, []);
  return <Context.Provider value={{ ...value, retry }}>{children}</Context.Provider>;
}
export const useServiceAvailability = () => useContext(Context);
